import React, { useState, useEffect, lazy, Suspense } from 'react';
import { ThemeProvider } from 'styled-components';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GlobalStyles } from './styles/GlobalStyles';
import { theme as lightTheme, darkTheme } from './styles/theme';
import AuthModal from './components/auth/AuthModal';
import MainLayout from './components/layout/MainLayout';
import SimpleLayout from './components/layout/SimpleLayout';
import { ModalProvider, useModal } from './context/ModalContext';

// Lazy load all route components
const Home = lazy(() => import('./components/pages/Home'));
const JsonValidator = lazy(() => import('./components/json/JsonValidator'));
const JsonConverter = lazy(() => import('./components/json/convertor/JsonConverter'));
const JsonCompare = lazy(() => import('./components/json/JsonCompare'));
const JsonViewer = lazy(() => import('./components/json/JsonViewer/JsonViewer'));
const JsonSchema = lazy(() => import('./components/json/JsonSchema'));
const JsonTreeVisualizer = lazy(() => import('./components/json/JsonTreeVisualizer'));
const JsonQuery = lazy(() => import('./components/json/JsonQuery'));
const TestDataGenerator = lazy(() => import('./components/json/datagenerator/TestDataGenerator'));
const Contact = lazy(() => import('./components/pages/Contact'));
const Feedback = lazy(() => import('./components/pages/Feedback'));
const Documentation = lazy(() => import('./components/pages/Documentation'));
const About = lazy(() => import('./components/pages/About'));
const Learn = lazy(() => import('./components/pages/Learn'));
const ApiTester = lazy(() => import('./components/json/ApiTester'));
const FakeRestApi = lazy(() => import('./components/json/FakeRestApi'));
const JsonSchemaValidator = lazy(() => import('./components/json/JsonSchemaValidator'));
const JsonInspector = lazy(() => import('./components/json/JsonInspector'));
const JsonScore = lazy(() => import('./components/json/JsonScore/JsonScore'));
const JsonEncoder = lazy(() => import('./components/json/JsonEncoder'));
const CodeGenerator = lazy(() => import('./components/json/CodeGenerator'));
const SecurityScanner = lazy(() => import('./components/json/SecurityScanner'));
const JsonMasker = lazy(() => import('./components/json/JsonMasker'));

// Legal pages
const Privacy = lazy(() => import('./components/pages/Privacy'));
const Terms = lazy(() => import('./components/pages/Terms'));
const Cookies = lazy(() => import('./components/pages/Cookies'));

// Hosting components
const JsonHosting = lazy(() => import('./components/json/hosting/JsonHosting'));
const JsonHostingDashboard = lazy(() => import('./components/json/hosting/JsonHostingDashboard'));

// Inner component that uses the modal context
const AppContent = () => {
  const [sidebarState, setSidebarState] = useState('collapsed');
  const [currentTheme, setCurrentTheme] = useState('light');
  const [user, setUser] = useState(null);
  const { isLoginModalOpen, closeLoginModal, openLoginModal } = useModal();

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth <= 768;
      const tablet = window.innerWidth > 768 && window.innerWidth <= 1024;

      if (mobile) {
        setSidebarState('hidden');
      } else if (tablet) {
        setSidebarState('collapsed');
      }
      // For desktop (width > 1024), do nothing – keep current state (which is 'collapsed')
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Check existing session
  useEffect(() => {
    const email = localStorage.getItem('email');
    const userName = localStorage.getItem('userName');
    const accessToken = localStorage.getItem('accessToken');

    if (email && accessToken) {
      setUser({
        email: email,
        name: userName || email.split('@')[0]
      });
    }
  }, []);

  const toggleSidebar = (newState) => {
    if (newState) {
      setSidebarState(newState);
    } else {
      const states = ['expanded', 'collapsed', 'hidden'];
      const currentIndex = states.indexOf(sidebarState);
      const nextIndex = (currentIndex + 1) % states.length;
      setSidebarState(states[nextIndex]);
    }
  };

  const toggleTheme = () => {
    setCurrentTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    closeLoginModal();
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('email');
    localStorage.removeItem('userName');
    setUser(null);
  };

  const currentThemeObj = currentTheme === 'light' ? lightTheme : darkTheme;

  return (
    <ThemeProvider theme={{ ...currentThemeObj, mode: currentTheme }}>
      <GlobalStyles />

      <AuthModal
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
        onLoginSuccess={handleLoginSuccess}
        user={user}
      />

      <Routes>
        <Route path="/" element={
          <SimpleLayout
            theme={currentTheme}
            user={user}
            onLoginClick={openLoginModal}
            onLogout={handleLogout}
          >
            <Suspense fallback={<div>Loading...</div>}>
              <Home />
            </Suspense>
          </SimpleLayout>
        } />

        <Route path="/" element={
          <MainLayout
            sidebarState={sidebarState}
            toggleSidebar={toggleSidebar}
            theme={currentTheme}
            toggleTheme={toggleTheme}
            user={user}
            onLoginClick={openLoginModal}
            onLogout={handleLogout}
          />
        }>
          <Route path="validator" element={<Suspense><JsonValidator /></Suspense>} />
          <Route path="converter" element={<Suspense><JsonConverter /></Suspense>} />
          <Route path="compare" element={<Suspense><JsonCompare /></Suspense>} />
          <Route path="viewer" element={<Suspense><JsonViewer /></Suspense>} />
          <Route path="schema" element={<Suspense><JsonSchema /></Suspense>} />
          <Route path="query" element={<Suspense><JsonQuery /></Suspense>} />
          <Route path="visualizer" element={<Suspense><JsonTreeVisualizer /></Suspense>} />
          <Route path="hosting" element={<Suspense><JsonHosting /></Suspense>} />
          <Route path="generator" element={<Suspense><TestDataGenerator /></Suspense>} />
          <Route path="api-tester" element={<Suspense><ApiTester /></Suspense>} />
          <Route path="fake-api" element={<Suspense><FakeRestApi /></Suspense>} />
          <Route path="inspector" element={<Suspense><JsonInspector /></Suspense>} />
          <Route path="schema-validator" element={<Suspense><JsonSchemaValidator /></Suspense>} />
          <Route path="json-score" element={<Suspense><JsonScore /></Suspense>} />
          <Route path="encoder" element={<Suspense><JsonEncoder /></Suspense>} />
          <Route path="code-generator" element={<Suspense><CodeGenerator /></Suspense>} />
          <Route path="security-scanner" element={<Suspense><SecurityScanner /></Suspense>} />
          <Route path="masker" element={<Suspense><JsonMasker /></Suspense>} />
          <Route path="compare/:id" element={<JsonCompare />} />
        </Route>

        <Route
          path="/hosting/dashboard"
          element={
            <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
              <Suspense fallback={<div style={{ padding: '2rem' }}>Loading dashboard...</div>}>
                <JsonHostingDashboard />
              </Suspense>
            </div>
          }
        />

        <Route path="/contact" element={
          <SimpleLayout
            theme={currentTheme}
            user={user}
            onLoginClick={openLoginModal}
            onLogout={handleLogout}
          >
            <Suspense><Contact /></Suspense>
          </SimpleLayout>
        } />
        <Route path="/feedback" element={
          <SimpleLayout
            theme={currentTheme}
            user={user}
            onLoginClick={openLoginModal}
            onLogout={handleLogout}
          >
            <Suspense><Feedback /></Suspense>
          </SimpleLayout>
        } />
        <Route path="/documentation" element={
          <SimpleLayout
            theme={currentTheme}
            user={user}
            onLoginClick={openLoginModal}
            onLogout={handleLogout}
          >
            <Suspense><Documentation /></Suspense>
          </SimpleLayout>
        } />
        <Route path="/about" element={
          <SimpleLayout
            theme={currentTheme}
            user={user}
            onLoginClick={openLoginModal}
            onLogout={handleLogout}
          >
            <Suspense><About /></Suspense>
          </SimpleLayout>
        } />
        <Route path="/learn" element={
          <SimpleLayout
            theme={currentTheme}
            user={user}
            onLoginClick={openLoginModal}
            onLogout={handleLogout}
          >
            <Suspense><Learn /></Suspense>
          </SimpleLayout>
        } />

        {/* Legal Pages */}
        <Route path="/privacy" element={
          <SimpleLayout
            theme={currentTheme}
            user={user}
            onLoginClick={openLoginModal}
            onLogout={handleLogout}
          >
            <Suspense><Privacy /></Suspense>
          </SimpleLayout>
        } />
        <Route path="/terms" element={
          <SimpleLayout
            theme={currentTheme}
            user={user}
            onLoginClick={openLoginModal}
            onLogout={handleLogout}
          >
            <Suspense><Terms /></Suspense>
          </SimpleLayout>
        } />
        <Route path="/cookies" element={
          <SimpleLayout
            theme={currentTheme}
            user={user}
            onLoginClick={openLoginModal}
            onLogout={handleLogout}
          >
            <Suspense><Cookies /></Suspense>
          </SimpleLayout>
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ThemeProvider>
  );
};

function App() {
  return (
    <ModalProvider>
      <AppContent />
    </ModalProvider>
  );
}

export default App;